// מסך 4 — ספריית אתרים ומיקומים, גרסאות מפרט, click adapters, ייבוא מפרט.
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { Empty, ErrorBox, Field, Loading, Modal, fmtDate, useLoad, useMe } from '../components/ui';
import { PageHead, SpecStatusBadge, Tabs, useAction } from '../components/mgmt/common';
import { AdapterModal, DEVICES, PlacementModal, SiteModal, adapterSummary, type ClickAdapter, type Placement, type Site } from '../components/mgmt/MediaParts';
import { SpecImport } from '../components/mgmt/SpecImport';

type Tab = 'sites' | 'adapters';

export default function MediaLibrary() {
  const me = useMe();
  const canEdit = me.role !== 'viewer';
  const [sp, setSp] = useSearchParams();
  const tab = (sp.get('tab') as Tab) || 'sites';
  const setTab = (t: Tab) => setSp((p) => { p.set('tab', t); return p; }, { replace: true });
  const sites = useLoad<Site[]>('/api/media-sites');
  const adapters = useLoad<ClickAdapter[]>('/api/click-adapters');
  const [q, setQ] = useState('');
  const [siteModal, setSiteModal] = useState<Site | 'new' | null>(null);
  const [placementModal, setPlacementModal] = useState<{ siteId: string; placement?: Placement } | null>(null);
  const [importSite, setImportSite] = useState<Site | null>(null);
  const [draftFor, setDraftFor] = useState<Placement | null>(null);
  const [adapterModal, setAdapterModal] = useState(false);
  const approve = useAction();

  const adapterById = Object.fromEntries((adapters.data ?? []).map((a) => [a.id, a]));
  const shown = (sites.data ?? []).filter((s) => !q || s.name.includes(q) || s.media_key.includes(q.toLowerCase()) || s.domain.includes(q.toLowerCase()));

  return (
    <div>
      <PageHead title="אתרים ומפרטים" sub="אין להמציא מפרטים. שדה שלא נמצא נשמר כ'לא ידוע'. מפרט נכנס לשימוש רק לאחר אישור.">
        {canEdit && tab === 'sites' && <button className="btn primary" onClick={() => setSiteModal('new')}>אתר חדש</button>}
        {canEdit && tab === 'adapters' && <button className="btn primary" onClick={() => setAdapterModal(true)}>שיטת קליק חדשה</button>}
      </PageHead>
      <Tabs<Tab> label="ספריית מדיה" value={tab} onChange={setTab} tabs={[{ id: 'sites', label: `אתרים (${sites.data?.length ?? 0})` }, { id: 'adapters', label: `שיטות קליק (${adapters.data?.length ?? 0})` }]} />

      {tab === 'sites' && (
        <div role="tabpanel">
          <div className="toolbar"><Field label="חיפוש אתר"><input type="search" value={q} onChange={(e) => setQ(e.target.value)} /></Field></div>
          <ErrorBox error={sites.error} />
          {sites.loading && !sites.data && <Loading />}
          {sites.data && shown.length === 0 && <Empty>אין אתרים. הוסיפו אתר ומיקומים, ואז ייבאו מפרט.</Empty>}
          {shown.map((s) => (
            <section key={s.id} className="card" aria-labelledby={`site-${s.id}`}>
              <div className="row between" style={{ alignItems: 'flex-start' }}>
                <div className="grow">
                  <h2 id={`site-${s.id}`} style={{ margin: 0 }}>{s.name} {s.is_demo && <span className="badge warn">הדגמה בלבד</span>}</h2>
                  <div className="small muted"><span className="mono">{s.media_key}</span>{s.domain && <> · <span className="ltr">{s.domain}</span></>}{s.site_url && <> · <a href={s.site_url} target="_blank" rel="noopener noreferrer" className="ltr">אתר</a></>}</div>
                  {s.spec_links.length > 0 && <div className="small">קישורי מפרט: {s.spec_links.map((l, i) => <a key={i} href={l} target="_blank" rel="noopener noreferrer" className="ltr" style={{ marginInlineEnd: 8 }}>{shortUrl(l)}</a>)}</div>}
                  {s.contacts.length > 0 && <div className="small muted">אנשי קשר: {s.contacts.map((c) => [c.name, c.role, c.email, c.phone].filter(Boolean).join(' · ')).join(' | ')}</div>}
                  {s.notes && <div className="small" style={{ whiteSpace: 'pre-wrap' }}>{s.notes}</div>}
                </div>
                {canEdit && (
                  <div className="row">
                    <button className="btn primary" onClick={() => setImportSite(s)}>ייבוא מפרט</button>
                    <button className="btn" onClick={() => setPlacementModal({ siteId: s.id })}>מיקום חדש</button>
                    <button className="btn" onClick={() => setSiteModal(s)}>עריכה</button>
                  </div>
                )}
                {!canEdit && <button className="btn" onClick={() => setImportSite(s)}>מקורות מפרט</button>}
              </div>
              {s.placements.length === 0 ? <p className="muted small">אין מיקומים.</p> : (
                <table className="table cards-sm" style={{ marginTop: 10 }}>
                  <thead><tr><th>מיקום</th><th>מכשיר</th><th>שיטת קליק</th><th>מפרט נוכחי</th><th>גרסאות</th><th></th></tr></thead>
                  <tbody>{s.placements.map((p) => {
                    const ad = p.click_adapter_id ? adapterById[p.click_adapter_id] : null;
                    return (
                      <tr key={p.id}>
                        <td><strong>{p.name}</strong><div className="mono small">{p.placement_key}</div></td>
                        <td data-l="מכשיר">{DEVICES[p.device] ?? p.device}</td>
                        <td data-l="שיטת קליק">
                          {!p.click_adapter_id && <span className="badge err">לא הוגדר — דורש קונפיגורציה ואישור</span>}
                          {ad && <>{ad.name} {ad.status === 'approved' ? <span className="badge ok">מאושר</span> : <span className="badge warn">לא מאושר</span>}{ad.is_demo && <span className="badge warn">הדגמה</span>}</>}
                          {p.click_adapter_id && !ad && adapters.data && <span className="badge err">שיטת קליק לא מוכרת</span>}
                        </td>
                        <td data-l="מפרט נוכחי">
                          {p.current_spec_version_id
                            ? <Link to={`/spec-versions/${p.current_spec_version_id}`}>גרסה {p.spec_version}</Link>
                            : <span className="small muted">אין מפרט מאושר</span>}{' '}
                          {p.current_spec_version_id && <SpecStatusBadge status={p.spec_status} isDemo={!!p.spec_is_demo} />}
                        </td>
                        <td data-l="גרסאות">
                          <div className="row" style={{ gap: 4 }}>
                            {(p.versions ?? []).map((v) => (
                              <Link key={v.id} to={`/spec-versions/${v.id}`} className="small" title={`גרסה ${v.version}`}>
                                v{v.version} <SpecStatusBadge status={v.status} />
                              </Link>
                            ))}
                            {!(p.versions ?? []).length && <span className="small muted">—</span>}
                          </div>
                        </td>
                        <td>{canEdit && <div className="row"><button className="btn small" onClick={() => setPlacementModal({ siteId: s.id, placement: p })}>עריכה</button><button className="btn small" onClick={() => setDraftFor(p)}>טיוטה ידנית</button></div>}</td>
                      </tr>
                    );
                  })}</tbody>
                </table>
              )}
            </section>
          ))}
        </div>
      )}

      {tab === 'adapters' && (
        <div role="tabpanel" className="stack">
          <div className="alert info small">שיטת קליק (click adapter) מתרגמת את הכתובת המתויגת לחתימת הקליק שהמדיה דורשת. adapter לא מוכר או לא מאושר דורש קונפיגורציה ואישור לפני יצוא למסירה.</div>
          <ErrorBox error={adapters.error} />
          <ErrorBox error={approve.error} />
          {adapters.loading && !adapters.data && <Loading />}
          {adapters.data && adapters.data.length === 0 && <Empty>אין שיטות קליק.</Empty>}
          {adapters.data && adapters.data.length > 0 && (
            <table className="table cards-sm">
              <thead><tr><th>שם</th><th>הגדרה</th><th>סטטוס</th><th></th></tr></thead>
              <tbody>{adapters.data.map((a) => (
                <tr key={a.id}>
                  <td><strong>{a.name}</strong>{a.is_demo && <> <span className="badge warn">הדגמה בלבד</span></>}</td>
                  <td data-l="הגדרה" className="ltr mono">{adapterSummary(a.config)}</td>
                  <td data-l="סטטוס">{a.status === 'approved' ? <><span className="badge ok">מאושר</span> <span className="small muted">{fmtDate(a.approved_at)}</span></> : <span className="badge warn">טיוטה — דורש אישור</span>}</td>
                  <td>
                    {a.status !== 'approved' && (me.canApproveSpecs
                      ? <button className="btn small primary" disabled={approve.busy} onClick={async () => { if (window.confirm(`לאשר את ה-adapter "${a.name}"? ודאו שההגדרה תואמת את מפרט המדיה.`)) { const r = await approve.run(() => api.post(`/api/click-adapters/${a.id}/approve`), 'ה-adapter אושר'); if (r) adapters.reload(); } }}>אישור</button>
                      : <span className="small muted">נדרשת הרשאת אישור מפרטים</span>)}
                  </td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </div>
      )}

      {siteModal && <SiteModal site={siteModal === 'new' ? undefined : siteModal} onClose={() => setSiteModal(null)} onSaved={sites.reload} />}
      {placementModal && <PlacementModal siteId={placementModal.siteId} placement={placementModal.placement} adapters={adapters.data ?? []} onClose={() => setPlacementModal(null)} onSaved={sites.reload} />}
      {adapterModal && <AdapterModal onClose={() => setAdapterModal(false)} onSaved={adapters.reload} />}
      {importSite && <SpecImport site={(sites.data ?? []).find((s) => s.id === importSite.id) ?? importSite} onClose={() => setImportSite(null)} onChanged={sites.reload} />}
      {draftFor && <ManualDraftModal placement={draftFor} onClose={() => setDraftFor(null)} />}
    </div>
  );
}

function ManualDraftModal({ placement, onClose }: { placement: Placement; onClose: () => void }) {
  const nav = useNavigate();
  const versions = placement.versions ?? [];
  const [copyFrom, setCopyFrom] = useState('');
  const { busy, error, run } = useAction();
  const create = async () => {
    const r = await run(() => api.post<{ id: string }>(`/api/placements/${placement.id}/spec-versions`, copyFrom ? { copyFrom } : { rules: [] }), 'נוצרה טיוטת מפרט');
    if (r) nav(`/spec-versions/${r.id}`);
  };
  return (
    <Modal title={`טיוטת מפרט ידנית — ${placement.name}`} onClose={onClose}>
      <div className="stack">
        <p className="small">טיוטה ידנית מאפשרת להזין שדות בעצמכם (מקור "הוזן ידנית"). כל שדה שלא יוזן יישאר "לא ידוע".</p>
        <Field label="בסיס">
          <select value={copyFrom} onChange={(e) => setCopyFrom(e.target.value)}>
            <option value="">טיוטה ריקה</option>
            {versions.map((v) => <option key={v.id} value={v.id}>העתקה מגרסה {v.version}</option>)}
          </select>
        </Field>
        <ErrorBox error={error} />
        <div className="row"><button className="btn primary" onClick={create} disabled={busy}>{busy ? 'יוצר…' : 'יצירה ומעבר לעריכה'}</button><button className="btn" onClick={onClose}>ביטול</button></div>
      </div>
    </Modal>
  );
}

function shortUrl(u: string) { try { const x = new URL(u); return x.hostname + (x.pathname.length > 1 ? x.pathname.slice(0, 24) : ''); } catch { return u; } }
